import { getHeroScheduleState } from './islander-hero-schedule.js';

export function getHomeSlideState(slideCount, requestedIndex) {
  const safeCount = Math.max(1, Number(slideCount) || 1);
  const rawIndex = Number(requestedIndex) || 0;
  const index = ((rawIndex % safeCount) + safeCount) % safeCount;

  return {
    index,
    previousIndex: (index - 1 + safeCount) % safeCount,
    nextIndex: (index + 1) % safeCount,
    positionLabel: `${index + 1} of ${safeCount}`,
  };
}

export function getHomeSwipeDirection(startX, endX, deltaY = 0, minimumDistance = 44) {
  const horizontalDistance = Number(startX) - Number(endX);
  const verticalDistance = Math.abs(Number(deltaY) || 0);
  const requiredDistance = Math.max(0, Number(minimumDistance) || 0);

  if (Math.abs(horizontalDistance) < requiredDistance || Math.abs(horizontalDistance) <= verticalDistance * 1.25) {
    return 0;
  }

  return horizontalDistance > 0 ? 1 : -1;
}

export function getHomeRailState(scrollWidth, clientWidth, scrollLeft, tolerance = 3) {
  const maxScroll = Math.max(0, Number(scrollWidth) - Number(clientWidth));
  const currentScroll = Math.max(0, Number(scrollLeft) || 0);
  const safeTolerance = Math.max(0, Number(tolerance) || 0);

  return {
    maxScroll,
    hasOverflow: maxScroll > safeTolerance,
    atStart: currentScroll <= safeTolerance,
    atEnd: currentScroll >= maxScroll - safeTolerance,
  };
}

export function getContinuousGalleryState(scrollLeft, loopWidth) {
  const position = Math.max(0, Number(scrollLeft) || 0);
  const safeLoopWidth = Math.max(0, Number(loopWidth) || 0);

  if (!safeLoopWidth || position < safeLoopWidth) {
    return { position, wrapped: false };
  }

  return {
    position: position % safeLoopWidth,
    wrapped: true,
  };
}

export function getContinuousRailPosition(scrollLeft, loopWidth) {
  const safeLoopWidth = Math.max(0, Number(loopWidth) || 0);
  if (!safeLoopWidth) return Math.max(0, Number(scrollLeft) || 0);

  const position = Number(scrollLeft) || 0;
  return ((position % safeLoopWidth) + safeLoopWidth) % safeLoopWidth;
}

if (typeof window !== 'undefined' && typeof customElements !== 'undefined') {
  class IslanderHomeHero extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.slides = [...this.querySelectorAll('[data-home-slide]')];
      this.indicators = [...this.querySelectorAll('[data-home-indicator]')];
      this.allSlides = [...this.slides];
      this.allIndicators = [...this.indicators];
      this.controls = this.querySelector('.islander-home-hero__controls');
      this.designMode = this.dataset.designMode === 'true';
      this.previousButton = this.querySelector('[data-home-previous]');
      this.nextButton = this.querySelector('[data-home-next]');
      this.pauseButton = this.querySelector('[data-home-pause]');
      this.status = this.querySelector('[data-home-status]');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.manualPaused = false;
      this.interactionPaused = false;
      this.pointerStart = null;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.rotationDelay = Math.max(3000, Number(this.dataset.rotationDelay) || 3000);

      const { signal } = this.abortController;

      this.previousButton?.addEventListener('click', () => this.move(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.move(1), { signal });
      this.pauseButton?.addEventListener('click', () => this.togglePause(), { signal });
      this.indicators.forEach((indicator) => {
        indicator.addEventListener('click', () => this.show(Number(indicator.dataset.homeIndicator)), { signal });
      });
      this.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      this.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
      this.addEventListener('pointercancel', () => this.resetPointer(), { signal });

      this.addEventListener('mouseenter', () => this.setInteractionPaused(true), { signal });
      this.addEventListener('mouseleave', () => this.setInteractionPaused(false), { signal });
      this.addEventListener('focusin', () => this.setInteractionPaused(true), { signal });
      this.addEventListener(
        'focusout',
        (event) => {
          if (!this.contains(event.relatedTarget)) this.setInteractionPaused(false);
        },
        { signal },
      );

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) {
            this.stopRotation();
            window.clearTimeout(this.scheduleTimer);
          } else {
            this.refreshSchedule();
            this.startRotation();
          }
          this.syncMedia();
        },
        { signal },
      );
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const selectedSlide = event.target.closest?.('[data-home-slide]');
          if (!selectedSlide || !this.contains(selectedSlide)) return;
          this.manualPaused = true;
          this.show(Number(selectedSlide.dataset.slideIndex));
          this.updatePauseControl();
        },
        { signal },
      );

      this.handleMotionChange = () => {
        this.stopRotation();
        this.startRotation();
      };
      this.motionQuery.addEventListener('change', this.handleMotionChange);

      this.refreshSchedule();
      this.startRotation();
    }

    disconnectedCallback() {
      window.clearTimeout(this.scheduleTimer);
      this.stopRotation();
      window.clearTimeout(this.transitionTimer);
      this.motionQuery?.removeEventListener('change', this.handleMotionChange);
      this.abortController?.abort();
      this.abortController = null;
    }

    refreshSchedule() {
      window.clearTimeout(this.scheduleTimer);
      const now = Date.now();
      const currentSlide = this.slides[this.index];
      const eligible = [];
      const indicators = [];
      let nextChange = null;
      this.allSlides.forEach((slide, originalIndex) => {
        const state = getHeroScheduleState({
          enabled: slide.dataset.scheduleEnabled === 'true',
          start: slide.dataset.scheduleStart,
          end: slide.dataset.scheduleEnd,
        }, now);
        const available = this.designMode || state.active;
        slide.hidden = !available;
        const indicator = this.allIndicators[originalIndex];
        if (indicator) indicator.hidden = !available;
        const note = slide.querySelector('[data-schedule-note]');
        if (note) note.textContent = !state.valid
          ? 'Invalid schedule: this slide will stay hidden from customers. Check both UK dates and times.'
          : state.active ? 'Schedule: currently available to customers.' : 'Schedule: currently hidden from customers. Shown here for editing.';
        if (available) {
          slide.dataset.slideIndex = String(eligible.length);
          if (indicator) {
            indicator.dataset.homeIndicator = String(eligible.length);
            indicator.setAttribute('aria-label', 'Show slide ' + (eligible.length + 1));
            indicators.push(indicator);
          }
          eligible.push(slide);
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.classList.remove('is-active', 'is-preparing-left', 'is-preparing-right', 'is-exiting-left', 'is-exiting-right');
          slide.querySelector('video')?.pause();
        }
        if (state.nextChange !== null) nextChange = Math.min(nextChange ?? Infinity, state.nextChange);
      });
      const changed = eligible.length !== this.slides.length || eligible.some((slide, i) => slide !== this.slides[i]);
      this.slides = eligible;
      this.indicators = indicators;
      this.hidden = !eligible.length;
      if (this.controls) this.controls.hidden = eligible.length < 2;
      eligible.forEach((slide, i) => slide.setAttribute('aria-label', (i + 1) + ' of ' + eligible.length));
      if (changed || !this.scheduleInitialized) {
        this.stopRotation();
        window.clearTimeout(this.transitionTimer);
        this.index = Math.max(0, eligible.indexOf(currentSlide));
        this.show(this.index, false);
        this.startRotation();
      }
      this.scheduleInitialized = true;
      if (!this.designMode && this.allSlides.some(slide => slide.dataset.scheduleEnabled === 'true') && !document.hidden) {
        // Recheck after sleep/clock changes, and at the exact next start/end boundary.
        this.scheduleTimer = window.setTimeout(() => this.refreshSchedule(), Math.max(1, Math.min(60000, (nextChange ?? now + 60000) - now)));
      }
    }

    move(direction) {
      const state = getHomeSlideState(this.slides.length, this.index);
      this.show(direction < 0 ? state.previousIndex : state.nextIndex, true, direction);
    }

    show(requestedIndex, restartRotation = true, requestedDirection = null) {
      if (!this.slides.length) return;

      const state = getHomeSlideState(this.slides.length, requestedIndex);
      const previousIndex = this.index;
      const direction = requestedDirection ?? (state.index < previousIndex ? -1 : 1);
      const shouldAnimate = state.index !== previousIndex && !this.motionQuery.matches;
      this.index = state.index;

      window.clearTimeout(this.transitionTimer);
      this.slides.forEach((slide, index) => {
        const isActive = index === state.index;
        slide.setAttribute('aria-hidden', String(!isActive));
        slide.classList.remove(
          'is-active',
          'is-preparing-left',
          'is-preparing-right',
          'is-exiting-left',
          'is-exiting-right',
        );
      });

      const activeSlide = this.slides[state.index];
      const previousSlide = this.slides[previousIndex];
      if (!shouldAnimate) {
        activeSlide.classList.add('is-active');
      } else {
        previousSlide.classList.add(direction > 0 ? 'is-exiting-left' : 'is-exiting-right');
        activeSlide.classList.add(direction > 0 ? 'is-preparing-right' : 'is-preparing-left');
        activeSlide.getBoundingClientRect();
        activeSlide.classList.remove('is-preparing-right', 'is-preparing-left');
        activeSlide.classList.add('is-active');
        this.transitionTimer = window.setTimeout(() => {
          previousSlide.classList.remove('is-exiting-left', 'is-exiting-right');
        }, 650);
      }
      this.indicators.forEach((indicator, index) => {
        const isActive = index === state.index;
        indicator.classList.toggle('is-active', isActive);
        indicator.setAttribute('aria-current', String(isActive));
      });
      if (this.status) this.status.textContent = state.positionLabel;
      this.syncMedia();

      if (restartRotation) {
        this.stopRotation();
        this.startRotation();
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' || !event.isPrimary) return;
      this.pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }

    onPointerUp(event) {
      if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;

      const direction = getHomeSwipeDirection(
        this.pointerStart.x,
        event.clientX,
        event.clientY - this.pointerStart.y,
      );
      this.resetPointer();
      if (direction !== 0) this.move(direction);
    }

    resetPointer() {
      this.pointerStart = null;
    }

    togglePause() {
      this.manualPaused = !this.manualPaused;
      this.updatePauseControl();
      this.stopRotation();
      this.startRotation();
      this.syncMedia();
    }

    syncMedia() {
      this.slides.forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;

        const shouldPlay = index === this.index && !this.manualPaused && !document.hidden;
        if (!shouldPlay) {
          video.pause();
          return;
        }

        const playPromise = video.play();
        playPromise?.catch(() => {});
      });
    }

    updatePauseControl() {
      if (!this.pauseButton) return;

      const pauseLabel = this.pauseButton.dataset.pauseLabel || 'Pause slideshow';
      const playLabel = this.pauseButton.dataset.playLabel || 'Play slideshow';
      this.pauseButton.dataset.paused = String(this.manualPaused);
      this.pauseButton.setAttribute('aria-label', this.manualPaused ? playLabel : pauseLabel);
      const pauseIcon = this.pauseButton.querySelector('[data-home-pause-icon]');
      const playIcon = this.pauseButton.querySelector('[data-home-play-icon]');
      if (pauseIcon) pauseIcon.hidden = this.manualPaused;
      if (playIcon) playIcon.hidden = !this.manualPaused;
    }

    setInteractionPaused(paused) {
      this.interactionPaused = paused;
      if (paused) this.stopRotation();
      else this.startRotation();
    }

    startRotation() {
      const canRotate =
        this.dataset.autoRotate === 'true' &&
        this.slides.length > 1 &&
        !this.manualPaused &&
        !this.interactionPaused &&
        !this.motionQuery.matches &&
        !document.hidden;

      if (!canRotate || this.rotationTimer) return;
      this.rotationTimer = window.setInterval(() => {
        const state = getHomeSlideState(this.slides.length, this.index);
        this.show(state.nextIndex, false, 1);
      }, this.rotationDelay);
    }

    stopRotation() {
      if (!this.rotationTimer) return;
      window.clearInterval(this.rotationTimer);
      this.rotationTimer = null;
    }
  }

  class IslanderHomeRail extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.viewport = this.querySelector('[data-home-rail-viewport]');
      this.previousButton = this.querySelector('[data-home-rail-previous]');
      this.nextButton = this.querySelector('[data-home-rail-next]');
      this.controls = this.querySelector('[data-home-rail-controls]');
      this.status = this.querySelector('[data-home-rail-status]');
      if (!this.viewport) return;

      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.mobileAutoplay = this.dataset.mobileAutoplay === 'true';
      this.autoplayDelay = Math.max(3000, Number(this.dataset.autoplayDelay) || 5000);
      this.continuousLoop = this.dataset.continuousLoop === 'true';
      this.continuousSpeed = Math.min(28, Math.max(8, Number(this.dataset.continuousSpeed) || 16));
      this.autoplayPaused = false;
      this.pauseReasons = new Set();
      this.lastFrameTime = null;
      this.lastRailUpdateTime = 0;
      this.lastProgrammaticScrollTime = 0;
      this.loopWidth = 0;
      this.originalItems = [...this.viewport.querySelectorAll('.islander-home-rail__item')];

      if (this.continuousLoop && this.originalItems.length > 1) this.setupContinuousLoop();

      const { signal } = this.abortController;
      this.previousButton?.addEventListener('click', () => this.scroll(-1, true), { signal });
      this.nextButton?.addEventListener('click', () => this.scroll(1, true), { signal });
      this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      this.viewport.addEventListener('scrollend', () => this.finishControlScroll(), { signal });
      this.viewport.addEventListener('pointerdown', () => this.pauseForInteraction(), { signal });
      this.viewport.addEventListener('pointerup', () => this.resumeAfterInteraction(), { signal });
      this.viewport.addEventListener('pointercancel', () => this.resumeAfterInteraction(), { signal });
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.setContinuousPaused('hover', true);
      }, { signal });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.setContinuousPaused('hover', false);
      }, { signal });
      this.addEventListener('focusin', () => {
        this.pauseAutoplay();
        this.setContinuousPaused('focus', true);
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.resumeAutoplaySoon();
          this.setContinuousPaused('focus', false);
        }
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        this.setContinuousPaused('hidden', document.hidden);
        this.stopAutoplay();
        this.startAutoplay();
      }, { signal });
      this.handleRailMediaChange = () => {
        this.setContinuousPaused('reduced-motion', this.motionQuery.matches);
        this.stopAutoplay();
        this.startAutoplay();
      };
      this.motionQuery.addEventListener('change', this.handleRailMediaChange);
      this.mobileQuery.addEventListener('change', this.handleRailMediaChange);
      window.addEventListener('resize', () => this.refreshLoopMetrics(), { signal });
      this.setContinuousPaused('hidden', document.hidden);
      this.setContinuousPaused('reduced-motion', this.motionQuery.matches);
      this.update();
      this.startAutoplay();
      this.startContinuousLoop();
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.animationFrame);
      window.cancelAnimationFrame(this.scrollUpdateFrame);
      this.stopAutoplay();
      window.clearTimeout(this.autoplayResumeTimer);
      window.clearTimeout(this.continuousResumeTimer);
      window.clearTimeout(this.controlScrollTimer);
      window.clearTimeout(this.controlResumeTimer);
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.motionQuery?.removeEventListener('change', this.handleRailMediaChange);
      this.mobileQuery?.removeEventListener('change', this.handleRailMediaChange);
      this.abortController?.abort();
      this.cloneItems?.forEach((item) => item.remove());
      this.classList.remove('is-continuous');
      this.abortController = null;
      this.animationFrame = null;
      this.lastFrameTime = null;
    }

    setupContinuousLoop() {
      const track = this.originalItems[0]?.parentElement;
      if (!track) return;

      this.cloneItems = this.originalItems.map((item) => {
        const clone = item.cloneNode(true);
        clone.classList.add('islander-home-rail__item--clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone
          .querySelectorAll('a, button, input, select, textarea, [tabindex]')
          .forEach((element) => element.setAttribute('tabindex', '-1'));
        track.append(clone);
        return clone;
      });
      this.classList.add('is-continuous');

      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.setContinuousPaused('offscreen', !entry?.isIntersecting);
      }, { rootMargin: '120px 0px', threshold: 0.01 });
      this.intersectionObserver.observe(this);

      this.resizeObserver = new ResizeObserver(() => this.refreshLoopMetrics());
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(track);
      this.refreshLoopMetrics();
    }

    refreshLoopMetrics() {
      if (this.cloneItems?.length && this.originalItems.length) {
        this.loopWidth = Math.max(0, this.cloneItems[0].offsetLeft - this.originalItems[0].offsetLeft);
        this.normalizeContinuousPosition();
      }
      this.update();
    }

    normalizeContinuousPosition() {
      if (!this.loopWidth) return;
      const position = getContinuousRailPosition(this.viewport.scrollLeft, this.loopWidth);
      if (Math.abs(position - this.viewport.scrollLeft) > 0.5) this.viewport.scrollLeft = position;
      this.continuousPosition = position;
    }

    onScroll() {
      const programmaticScroll = performance.now() - this.lastProgrammaticScrollTime < 80;
      if (this.continuousLoop && !programmaticScroll && !this.isControlScrolling) {
        this.normalizeContinuousPosition();
      }
      if (this.scrollUpdateFrame) return;

      this.scrollUpdateFrame = window.requestAnimationFrame((timestamp) => {
        this.scrollUpdateFrame = null;
        if (this.continuousLoop && timestamp - this.lastRailUpdateTime < 180) return;
        this.lastRailUpdateTime = timestamp;
        this.update();
      });
    }

    scroll(direction, restartAutoplay = false) {
      const behavior = this.motionQuery.matches ? 'auto' : 'smooth';
      const itemStep = this.getItemStep();
      if (this.continuousLoop && this.loopWidth) {
        this.setContinuousPaused('control', true);
        this.isControlScrolling = true;
        window.clearTimeout(this.controlScrollTimer);
        window.clearTimeout(this.controlResumeTimer);

        const currentPosition = getContinuousRailPosition(this.viewport.scrollLeft, this.loopWidth);
        const itemProgress = currentPosition / itemStep;
        let requestedPosition;

        if (direction > 0) {
          requestedPosition = Math.min(this.loopWidth, (Math.floor(itemProgress + 0.02) + 1) * itemStep);
        } else if (currentPosition <= 1) {
          this.viewport.scrollLeft = this.loopWidth;
          requestedPosition = Math.max(0, this.loopWidth - itemStep);
        } else {
          requestedPosition = Math.max(0, (Math.ceil(itemProgress - 0.02) - 1) * itemStep);
        }

        this.viewport.scrollTo({
          left: requestedPosition,
          behavior,
        });
        this.controlScrollTimer = window.setTimeout(() => this.finishControlScroll(), behavior === 'auto' ? 40 : 700);
      } else {
        this.viewport.scrollBy({ left: direction * itemStep, behavior });
      }
      if (restartAutoplay) {
        this.resumeAutoplaySoon();
        this.resumeAfterInteraction();
      }
    }

    finishControlScroll() {
      if (!this.isControlScrolling) return;
      window.clearTimeout(this.controlScrollTimer);
      this.isControlScrolling = false;
      this.normalizeContinuousPosition();
      this.update();
      window.clearTimeout(this.controlResumeTimer);
      this.controlResumeTimer = window.setTimeout(() => {
        this.setContinuousPaused('control', false);
      }, 1200);
    }

    getItemStep() {
      const firstItem = this.originalItems?.[0];
      const secondItem = this.originalItems?.[1];
      if (!firstItem) return this.viewport.clientWidth * 0.82;
      if (secondItem) return Math.abs(secondItem.offsetLeft - firstItem.offsetLeft);
      return firstItem.getBoundingClientRect().width || this.viewport.clientWidth * 0.82;
    }

    pauseAutoplay() {
      this.autoplayPaused = true;
      this.stopAutoplay();
      window.clearTimeout(this.autoplayResumeTimer);
    }

    resumeAutoplaySoon() {
      this.pauseAutoplay();
      this.autoplayResumeTimer = window.setTimeout(() => {
        this.autoplayPaused = false;
        this.startAutoplay();
      }, 4500);
    }

    pauseForInteraction() {
      this.pauseAutoplay();
      this.setContinuousPaused('interaction', true);
      window.clearTimeout(this.continuousResumeTimer);
    }

    resumeAfterInteraction() {
      this.resumeAutoplaySoon();
      window.clearTimeout(this.continuousResumeTimer);
      this.continuousResumeTimer = window.setTimeout(() => {
        this.setContinuousPaused('interaction', false);
      }, 1800);
    }

    setContinuousPaused(reason, paused) {
      if (!this.continuousLoop) return;
      if (paused) this.pauseReasons.add(reason);
      else this.pauseReasons.delete(reason);

      if (this.pauseReasons.size) {
        window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
        this.lastFrameTime = null;
        return;
      }
      this.startContinuousLoop();
    }

    startContinuousLoop() {
      if (!this.continuousLoop || !this.loopWidth || this.pauseReasons.size || this.animationFrame || !this.isConnected) return;
      this.animationFrame = window.requestAnimationFrame((timestamp) => this.tickContinuousLoop(timestamp));
    }

    tickContinuousLoop(timestamp) {
      this.animationFrame = null;
      if (!this.continuousLoop || this.pauseReasons.size || !this.isConnected) return;

      if (this.lastFrameTime !== null) {
        const elapsed = Math.min(50, Math.max(0, timestamp - this.lastFrameTime));
        const nextPosition = getContinuousRailPosition(
          (Number.isFinite(this.continuousPosition) ? this.continuousPosition : this.viewport.scrollLeft) +
            this.continuousSpeed * (elapsed / 1000),
          this.loopWidth,
        );
        this.continuousPosition = nextPosition;
        this.lastProgrammaticScrollTime = performance.now();
        this.isProgrammaticFrame = true;
        this.viewport.scrollLeft = nextPosition;
        this.isProgrammaticFrame = false;
      }
      this.lastFrameTime = timestamp;
      this.animationFrame = window.requestAnimationFrame((nextTimestamp) => this.tickContinuousLoop(nextTimestamp));
    }

    startAutoplay() {
      const canAutoplay =
        !this.continuousLoop &&
        this.mobileAutoplay &&
        this.mobileQuery.matches &&
        !this.motionQuery.matches &&
        !this.autoplayPaused &&
        !document.hidden &&
        this.viewport.scrollWidth > this.viewport.clientWidth + 3;

      if (!canAutoplay || this.autoplayTimer) return;
      this.autoplayTimer = window.setInterval(() => this.advanceAutoplay(), this.autoplayDelay);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    advanceAutoplay() {
      const state = getHomeRailState(this.viewport.scrollWidth, this.viewport.clientWidth, this.viewport.scrollLeft);
      const behavior = this.motionQuery.matches ? 'auto' : 'smooth';
      if (state.atEnd) {
        this.viewport.scrollTo({ left: 0, behavior });
        return;
      }
      this.viewport.scrollBy({ left: this.getItemStep(), behavior });
    }

    update() {
      const state = getHomeRailState(this.viewport.scrollWidth, this.viewport.clientWidth, this.viewport.scrollLeft);
      if (this.controls) this.controls.hidden = !state.hasOverflow;
      if (this.previousButton) this.previousButton.disabled = this.continuousLoop ? false : state.atStart;
      if (this.nextButton) this.nextButton.disabled = this.continuousLoop ? false : state.atEnd;

      const items = this.originalItems ?? [];
      const firstItem = items[0];
      if (!this.status || !firstItem || !items.length) return;

      const itemWidth = firstItem.getBoundingClientRect().width || this.viewport.clientWidth;
      const itemStep = Math.max(1, this.getItemStep());
      const scrollLeft = this.continuousLoop
        ? getContinuousRailPosition(this.viewport.scrollLeft, this.loopWidth)
        : this.viewport.scrollLeft;
      const firstVisible = Math.min(items.length - 1, Math.max(0, Math.round(scrollLeft / itemStep)));
      const visibleCount = Math.max(1, Math.floor((this.viewport.clientWidth + 1) / itemWidth));
      const lastVisible = Math.min(items.length, firstVisible + visibleCount);
      this.status.textContent = `${firstVisible + 1}–${lastVisible} of ${items.length}`;
    }
  }

  class IslanderHomeGallery extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.viewport = this.querySelector('[data-home-gallery-viewport]');
      this.group = this.querySelector('[data-home-gallery-group]');
      this.clone = this.querySelector('[data-home-gallery-clone]');
      this.enabled = this.dataset.galleryEnabled === 'true' && Boolean(this.viewport && this.group && this.clone);
      this.speed = Math.min(24, Math.max(6, Number(this.dataset.gallerySpeed) || 16));
      this.pauseReasons = new Set();
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.lastFrameTime = null;
      this.dragState = null;
      this.virtualPosition = this.viewport?.scrollLeft || 0;

      if (!this.viewport || !this.group) return;

      const { signal } = this.abortController;
      this.addEventListener(
        'pointerenter',
        (event) => {
          if (event.pointerType === 'mouse') this.setPaused('hover', true);
        },
        { signal },
      );
      this.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType === 'mouse') this.setPaused('hover', false);
        },
        { signal },
      );
      this.addEventListener('focusin', () => this.setPaused('focus', true), { signal });
      this.addEventListener(
        'focusout',
        (event) => {
          if (!this.contains(event.relatedTarget)) this.setPaused('focus', false);
        },
        { signal },
      );

      this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
      this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal });
      this.viewport.addEventListener('pointerup', (event) => this.onPointerEnd(event), { signal });
      this.viewport.addEventListener('pointercancel', (event) => this.onPointerEnd(event), { signal });
      this.viewport.addEventListener('lostpointercapture', (event) => this.onPointerEnd(event), { signal });

      document.addEventListener(
        'visibilitychange',
        () => this.setPaused('hidden', document.hidden),
        { signal },
      );
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          if (this.contains(event.target)) this.setPaused('editor', true);
        },
        { signal },
      );
      document.addEventListener(
        'shopify:block:deselect',
        (event) => {
          if (this.contains(event.target)) this.setPaused('editor', false);
        },
        { signal },
      );
      window.addEventListener('resize', () => this.normalizePosition(), { signal });

      this.handleMotionChange = () => this.setPaused('reduced-motion', this.motionQuery.matches);
      this.motionQuery.addEventListener('change', this.handleMotionChange);
      this.setPaused('reduced-motion', this.motionQuery.matches);
      this.setPaused('hidden', document.hidden);
      this.start();
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.animationFrame);
      window.clearTimeout(this.resumeTimer);
      this.motionQuery?.removeEventListener('change', this.handleMotionChange);
      this.abortController?.abort();
      this.abortController = null;
      this.animationFrame = null;
      this.lastFrameTime = null;
    }

    getLoopWidth() {
      if (!this.clone || !this.group) return 0;
      return Math.max(0, this.clone.offsetLeft - this.group.offsetLeft);
    }

    normalizePosition() {
      const state = getContinuousGalleryState(this.virtualPosition, this.getLoopWidth());
      this.virtualPosition = state.position;
      if (state.wrapped) this.viewport.scrollLeft = state.position;
      return state;
    }

    setPaused(reason, paused) {
      if (reason === 'interaction' && !paused && this.viewport) {
        this.virtualPosition = this.viewport.scrollLeft;
      }

      if (paused) this.pauseReasons.add(reason);
      else this.pauseReasons.delete(reason);

      if (this.pauseReasons.size) {
        window.cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
        this.lastFrameTime = null;
        return;
      }

      this.start();
    }

    start() {
      if (!this.enabled || this.pauseReasons.size || this.animationFrame) return;
      this.animationFrame = window.requestAnimationFrame((timestamp) => this.tick(timestamp));
    }

    tick(timestamp) {
      this.animationFrame = null;
      if (!this.enabled || this.pauseReasons.size || !this.isConnected) return;

      if (this.lastFrameTime !== null) {
        const elapsed = Math.min(64, Math.max(0, timestamp - this.lastFrameTime));
        this.virtualPosition += this.speed * (elapsed / 1000);
        this.normalizePosition();
        this.viewport.scrollLeft = this.virtualPosition;
      }

      this.lastFrameTime = timestamp;
      this.animationFrame = window.requestAnimationFrame((nextTimestamp) => this.tick(nextTimestamp));
    }

    onPointerDown(event) {
      if (!event.isPrimary) return;

      window.clearTimeout(this.resumeTimer);
      this.setPaused('interaction', true);
      if (event.pointerType !== 'mouse') return;

      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: this.viewport.scrollLeft,
      };
      this.viewport.setPointerCapture(event.pointerId);
      this.viewport.classList.add('is-dragging');
      event.preventDefault();
    }

    onPointerMove(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const loopWidth = this.getLoopWidth();
      let nextPosition = this.dragState.startScrollLeft - (event.clientX - this.dragState.startX);
      if (loopWidth && nextPosition < 0) nextPosition += loopWidth;
      this.virtualPosition = nextPosition;
      this.viewport.scrollLeft = nextPosition;
      this.normalizePosition();
      event.preventDefault();
    }

    onPointerEnd(event) {
      if (this.dragState && event.pointerId === this.dragState.pointerId) {
        if (this.viewport.hasPointerCapture?.(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
        this.dragState = null;
        this.viewport.classList.remove('is-dragging');
      }

      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.setPaused('interaction', false), 1000);
    }
  }

  if (!customElements.get('islander-home-hero')) customElements.define('islander-home-hero', IslanderHomeHero);
  if (!customElements.get('islander-home-rail')) customElements.define('islander-home-rail', IslanderHomeRail);
  if (!customElements.get('islander-home-gallery')) customElements.define('islander-home-gallery', IslanderHomeGallery);
}
